import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Subject, takeUntil, tap } from 'rxjs';
import { SimulationsBySupplier } from 'src/app/shared/objects/simulations-by-supplier';
import { GenericManufacturerSimulation } from 'src/app/shared/objects/generic-manufacturer-simulation';
import { CustomNumberFormatPipe } from 'src/app/shared/pipe/custom-number-format.pipe';
import { GenericManufacturerSimulationGlobalInformation } from 'src/app/shared/objects/generic-manufacturer-simulation-global-information';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { TooltipPosition } from 'src/app/shared/components/tooltip/tooltip.enums';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-manufacturer-table',
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatTabsModule,
    CustomNumberFormatPipe,
    MatTooltipModule,
    CommonModule,
    MatIconModule,
  ],
  templateUrl: './generic-manufacturer-table.component.html',
  styleUrl: './generic-manufacturer-table.component.scss',
})
export class GenericManufacturerTableComponent implements OnInit, OnDestroy {
  // Tooltip messages as readonly constants
  public static readonly TOOLTIP_MESSAGES = {
    msgTooltipAssiette: 'Regroupement des produits des lignes de factures',
    msgTooltipQuantite: 'Quantité de produit dans l’assiette',
    msgTooltipMontantBrutHT: 'Montant brut HT de l’assiette',
    msgTooltipRSFEuro: 'Remise sur facture en euros de l’assiette',
    msgTooltipRSFPercent: 'Calcul en % de la remise = (RSF € / Montant brut HT) * 100',
    msgTooltipMontantNetHT: 'Montant brut HT - RSF €',
    msgTooltipSimulationIndividuelle: 'Valeur en % à simuler (s’applique au Montant brut HT)',
    msgTooltipTotalSimulation:
      'Simulation à percevoir au total = (Simulation individuelle / 100) * Montant brut HT',
    msgTooltipSimulationRecevoir: 'Différence entre le Total Simulation € et RSF € déjà perçue',
    msgTooltipTotalPercu: 'Saisie du montant déjà perçu s’il y en a',
    msgTooltipResteARecevoir: 'Différence entre Simulation à recevoir et Total perçu',
  } as const;
  public readonly TOOLTIP_MESSAGES = GenericManufacturerTableComponent.TOOLTIP_MESSAGES;
  private readonly _destroying$: Subject<void> = new Subject<void>();
  private readonly _targetPercentageChange$ = new Subject<GenericManufacturerSimulation>();
  private readonly _amountCollectedChange$ = new Subject<number>();
  sortDirection: 'asc' | 'desc' = 'asc';
  protected readonly TooltipPosition = TooltipPosition;
  private _amountCollected: number = 0;
  private _globalTotalCooperation: number = 0;
  private _globalNetTotalSimulation = 0;
  @Output() targetPercentageChanged = new EventEmitter<{
    simulationId: number;
    newValue: number;
  }>();

  get amountToCollect(): number {
    return this._globalNetTotalSimulation - this._amountCollected;
  }

  get amountCollected(): number {
    return this._amountCollected;
  }

  get globalTotalCooperation(): number {
    return this._globalTotalCooperation;
  }

  get globalNetTotalSimulation(): number {
    return this._globalNetTotalSimulation;
  }
  @Input() selectedMonths: string[] | undefined = [];
  @Input({ required: true }) genericManufacturer!: SimulationsBySupplier<
    GenericManufacturerSimulation,
    GenericManufacturerSimulationGlobalInformation
  >;

  modifiedAmountCollected = (): boolean => {
    return this._amountCollected !== 0;
  };

  ngOnInit(): void {
    this._targetPercentageChange$
      .pipe(
        takeUntil(this._destroying$),
        tap(base => {
          this.calculateBaseTotalCooperation(base);
          this.calculateDiscountPercent(base);
        }),
        tap(() => {
          this.calculateGlobalTotalCooperation();
          this.calculateNetTotalSimulation();
        }),
      )
      .subscribe();

    this._amountCollectedChange$
      .pipe(
        takeUntil(this._destroying$),
        tap(amount => (this._amountCollected = amount)),
      )
      .subscribe();
    // Calcul du % de RSF pour toutes les simulations au démarrage
    if (this.genericManufacturer?.simulations) {
      this.genericManufacturer.simulations.forEach(sim => {
        this.calculateDiscountPercent(sim);
      });
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  focusNextInput(event: Event): void {
    event.preventDefault();

    const currentElement = event.target as HTMLElement;
    const formElements = Array.from(
      document.querySelectorAll<HTMLElement>('input, select, textarea, button, [tabindex]'),
    ).filter(el => !el.hasAttribute('disabled') && !el.getAttribute('tabindex')?.includes('-1'));

    const currentIndex = formElements.indexOf(currentElement);

    if (currentIndex !== -1 && currentIndex + 1 < formElements.length) {
      const nextElement = formElements[currentIndex + 1];
      nextElement.focus();
    }
  }

  onTargetPercentageChange(simulation: GenericManufacturerSimulation, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.parseLocalizedNumber(input.value);
    if (value !== 0) {
      simulation.grossPercentileTarget = value;
      this._targetPercentageChange$.next(simulation);
    }
  }

  onTargetPercentageBlur(base: GenericManufacturerSimulation, event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.parseLocalizeNumber(input.value);
    // if no input, we reset the input.value to "-" and don't start any simulation
    if (value == 0) {
      input.value = '-';

      // re initialize the value in the base object
      base.totalCooperation = 0;
      base.netTotalSimulation = 0;

      // relance les calculs
      this.calculateGlobalTotalCooperation(); // TOTAL "total Simulation €"
      this.calculateNetTotalSimulation(); // TOTAL "Simulation à recevoir"
      base.grossPercentileTarget = undefined;
      return;
    }
  }

  isPercentileTargetDisabled(base: GenericManufacturerSimulation): boolean {
    return base.grossAmountExclTax === 0;
  }

  onAmountCollectedBlur(event: Event): void {
    this._amountCollected = this.parseLocalizeNumber((event.target as HTMLInputElement).value);
    this._amountCollectedChange$.next(this._amountCollected);
  }

  getBaseNetAmountExclTax(base: GenericManufacturerSimulation): number {
    const calcNetAmount = (base.grossAmountExclTax ?? 0) - (base.discountEuroExclTax ?? 0);
    if (calcNetAmount < 0) {
      return 0;
    }
    return calcNetAmount;
  }

  getSimulatedRows() {
    return this.sortedSimulations.filter(
      base => base.grossPercentileTarget && base.grossPercentileTarget !== 0,
    );
  }

  getSimulatedTotalQuantity() {
    return this.getSimulatedRows().reduce((sum, base) => sum + (base.quantity || 0), 0);
  }

  getSimulatedTotalGrossAmountExclTax() {
    return this.getSimulatedRows().reduce((sum, base) => sum + (base.grossAmountExclTax || 0), 0);
  }

  getSimulatedTotalDiscountableEuroExclTax() {
    return this.getSimulatedRows().reduce((sum, base) => sum + (base.discountEuroExclTax || 0), 0);
  }

  getSimulatedTotalDiscountPercent() {
    const totalDiscount = this.getSimulatedTotalDiscountableEuroExclTax();
    const totalGross = this.getSimulatedTotalGrossAmountExclTax();
    return totalGross ? (totalDiscount / totalGross) * 100 : 0;
  }

  getSimulatedTotalNetAmountExclTax() {
    return this.getSimulatedRows().reduce(
      (sum, base) => sum + (this.getBaseNetAmountExclTax(base) || 0),
      0,
    );
  }

  getSimulatedTotalCooperation() {
    return this.getSimulatedRows().reduce((sum, base) => sum + (base.totalCooperation || 0), 0);
  }

  getSimulatedNetTotalSimulation() {
    return this.getSimulatedRows().reduce((sum, base) => sum + (base.netTotalSimulation || 0), 0);
  }

  getSimulatedIndividualPercent() {
    const totalSimulation = this.getSimulatedTotalCooperation();
    const totalGross = this.getSimulatedTotalGrossAmountExclTax();
    return totalGross ? (totalSimulation / totalGross) * 100 : '-';
  }

  getTotalQuantity(): number {
    return this.genericManufacturer.simulations.reduce(
      (acc, simulation) => acc + (simulation.quantity ?? 0),
      0,
    );
  }

  getTotalGrossAmountExclTax(): number {
    return this.genericManufacturer.simulations.reduce(
      (acc, simulation) => acc + (simulation.grossAmountExclTax ?? 0),
      0,
    );
  }

  getTotalDiscountableEurExclTax(): number {
    return this.genericManufacturer.simulations.reduce(
      (acc, simulation) => acc + (simulation.discountEuroExclTax ?? 0),
      0,
    );
  }

  getTotalNetAmountExclTax(): number {
    return this.genericManufacturer.simulations.reduce(
      (acc, simulation) => acc + (this.getBaseNetAmountExclTax(simulation) ?? 0),
      0,
    );
  }

  // Montant total brut remisable à l'écran
  getYearlyGrossTurnover(): number {
    return this.getGlobalSumValue('CA annuel brut');
  }

  // CA moyen brut mensuel à l'écran
  getAverageDiscountableGrossTurnover(): number {
    return this.getGlobalAverageValue('CA moyen achat brut remisable');
  }

  private getGlobalSumValue(label: string): number {
    // on calcule la somme totale pour ce label
    return (
      this.genericManufacturer.simulationGlobalInformation
        .filter(d => d.calculationName === label)
        .reduce((sum, item) => sum + Number(item.calculationResult ?? 0), 0) || 0
    );
  }
  getYearlyCreditAmountHT(): number {
    return this.getGlobalSumValue('Montant HT Avoir');
  }
  getCreditToInvoiceNetHTRatio(): number {
    const totalAvoirHT = this.getYearlyCreditAmountHT();
    const totalFactureHT = this.getTotalNetAmountExclTax();
    if (totalFactureHT === 0) {
      return 0; // éviter une division par zéro
    }
    return totalAvoirHT / totalFactureHT;
  }

  private getGlobalAverageValue(label: string): number {
    // on calcule la moyenne mensuelle pour ce label
    const filteredData = this.genericManufacturer.simulationGlobalInformation.filter(
      d => d.calculationName === label,
    );
    return (
      filteredData.reduce((sum, item) => sum + Number(item.calculationResult ?? 0), 0) /
        (this.selectedMonths?.length || filteredData.length) || 0
    );
  }

  private calculateGlobalTotalCooperation(): void {
    let calculatedAmount = this.genericManufacturer.simulations.reduce(
      (acc, simulation) => acc + (simulation.totalCooperation ?? 0),
      0,
    );

    this._globalTotalCooperation = calculatedAmount;
  }

  private parseLocalizeNumber(value: string): number {
    if (typeof value === 'number') {
      return value;
    } else if (typeof value === 'string') {
      value = value.replace(/ /g, '');

      let firstCommaIndex = value.indexOf(',');
      if (firstCommaIndex !== -1) {
        value = value.replace(/\./g, '');
        value = value.substring(0, firstCommaIndex) + '.' + value.substring(firstCommaIndex + 1);
      }

      const parsedValue = parseFloat(value);
      return isNaN(parsedValue) ? 0 : parsedValue;
    }

    return 0;
  }

  private calculateDiscountPercent(base: GenericManufacturerSimulation): void {
    if (base.grossAmountExclTax && base.discountEuroExclTax) {
      base.discountPercent = (base.discountEuroExclTax / base.grossAmountExclTax) * 100;
    } else {
      base.discountPercent = 0;
    }
  }

  private calculateBaseTotalCooperation(base: GenericManufacturerSimulation): void {
    let calculatedTotalCooperation;

    if (base.grossAmountExclTax && base.grossPercentileTarget) {
      calculatedTotalCooperation = (base.grossPercentileTarget / 100) * base.grossAmountExclTax;
    }

    base.totalCooperation = calculatedTotalCooperation ?? 0;

    if (base.discountEuroExclTax !== undefined) {
      base.netTotalSimulation = base.totalCooperation - base.discountEuroExclTax;
    } else {
      base.netTotalSimulation = base.totalCooperation;
    }
  }

  private calculateNetTotalSimulation(): void {
    this._globalNetTotalSimulation = this.genericManufacturer.simulations.reduce(
      (acc, d) => acc + (d.netTotalSimulation ?? 0),
      0,
    );
  }

  private parseLocalizedNumber(value: string): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      value = value.replace(/ /g, '');
      const commaIndex = value.indexOf(',');
      if (commaIndex !== -1) {
        value = value.replace(/\./g, '').replace(',', '.');
      }
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }

  get sortedSimulations(): GenericManufacturerSimulation[] {
    return [...this.genericManufacturer.simulations].sort((a, b) => {
      const orderA = a.ordreAssiette ?? 0;
      const orderB = b.ordreAssiette ?? 0;

      return this.sortDirection === 'asc' ? orderA - orderB : orderB - orderA;
    });
  }
  toggleSortDirection(): void {
    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
  }
}
